import { useEffect, useRef, useState } from 'react';
import { useAction, useApi, useIntentKey } from '../../api/hooks.js';
import {
  archiveArticle,
  createArticle,
  getArticle,
  listArticles,
  publishArticle,
  updateArticle,
  uploadArticleImage,
  type Article,
  type ArticleStatus,
} from '../api.js';
import {
  articleBody,
  isUploadedArticleImage,
  type ArticleBody,
  type ArticleForm,
  type ArticleImageRow,
  type ArticleSourceRow,
  type Problem,
} from '../forms.js';
import { fmtWhen, statusLabel } from '../labels.js';
import { adminHash } from '../routes.js';
import { Card, Empty, Field, Head, Loading, problemFor, Problems, ReasonStrip, show, Tag, Trouble } from '../ui.js';

/**
 * Articles for the app's feed. Every user sees every published article;
 * nothing here is per-person. Editing a published article changes it live;
 * publishing is one click; archiving takes a reason.
 *
 * PICTURES ARE UPLOADED, NEVER PASTED. Choosing a file presigns a PUT and
 * sends the bytes straight to S3; the form then holds the stored object URL,
 * which is the only kind of picture URL the backend accepts.
 *
 * "No imagery in the console" is intact. The thumbnail beside a row is the
 * operator's OWN file, previewed from a browser blob URL — bytes they picked
 * seconds ago and already have. Nothing held server-side is ever fetched and
 * rendered here: a picture already saved shows as a link, exactly as before.
 */

const LIST_LIMIT = 200;
const FILTERS: { value: ArticleStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'published', label: 'Published' },
  { value: 'archived', label: 'Archived' },
];

function statusTone(status: string): 'ok' | 'warn' | 'bad' | undefined {
  if (status === 'published') return 'ok';
  if (status === 'draft') return 'warn';
  if (status === 'archived') return 'bad';
  return undefined;
}

export function ArticlesScreen({ id }: { id: string | null }) {
  if (id === 'new') return <ArticleEditor article={null} />;
  if (id !== null) return <ArticleLoader id={id} />;
  return <ArticleList />;
}

function ArticleList() {
  const articles = useApi(() => listArticles(LIST_LIMIT), []);
  const [filter, setFilter] = useState<ArticleStatus | 'all'>('all');
  const publish = useAction(publishArticle, () => articles.reload());
  const archive = useAction((articleId: string, reason: string) => archiveArticle(articleId, reason), () =>
    articles.reload(),
  );

  const rows = (articles.data?.articles ?? []).filter((article) => filter === 'all' || article.status === filter);

  return (
    <>
      <Head
        title="Articles"
        lede="Articles for the feed in the app. A published article is visible to every user straight away; drafts and archived articles are visible to nobody."
        meta={
          <a className="cr-btn primary" href={adminHash('articles', 'new')}>
            New article
          </a>
        }
      />

      <Card
        title="All articles"
        aside={
          <div className="cr-tabs" role="group" aria-label="Filter by status">
            {FILTERS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="cr-tab"
                aria-pressed={filter === option.value}
                onClick={() => setFilter(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        }
      >
        {articles.error !== null ? (
          <Trouble error={articles.error} />
        ) : articles.data === null ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>{filter === 'all' ? 'No articles yet. Start with a new article.' : `No ${filter} articles.`}</Empty>
        ) : (
          <div className="cr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Last edited</th>
                  <th>By</th>
                  <th className="num">Pictures</th>
                  <th className="num">Sources</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((article) => (
                  <tr key={article.article_id}>
                    <td>
                      <a href={adminHash('articles', article.article_id)}>
                        <strong>{article.title}</strong>
                      </a>
                      <div className="cr-fine">{article.summary}</div>
                    </td>
                    <td>
                      <Tag tone={statusTone(article.status)}>{statusLabel(article.status)}</Tag>
                    </td>
                    <td>{fmtWhen(article.updated_at)}</td>
                    <td>{show(article.updated_by)}</td>
                    <td className="num">{String((article.images ?? []).length)}</td>
                    <td className="num">{String((article.sources ?? []).length)}</td>
                    <td style={{ minWidth: 320 }}>
                      <div className="cr-actions">
                        {article.status !== 'published' && (
                          <button
                            type="button"
                            className="cr-btn primary"
                            disabled={publish.pending}
                            onClick={() => publish.run(article.article_id)}
                          >
                            Publish
                          </button>
                        )}
                        <a className="cr-btn" href={adminHash('articles', article.article_id)}>
                          Edit
                        </a>
                      </div>
                      {article.status !== 'archived' && (
                        <ReasonStrip
                          verb="Archive"
                          tone="danger"
                          pending={archive.pending}
                          placeholder="Why it should leave the feed"
                          onRun={(reason) => archive.run(article.article_id, reason)}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {publish.error !== null && <Trouble error={publish.error} />}
        {archive.error !== null && <Trouble error={archive.error} />}
      </Card>
    </>
  );
}

function ArticleLoader({ id }: { id: string }) {
  const article = useApi(() => getArticle(id), [id]);
  if (article.error !== null) {
    return (
      <>
        <Head title="Article" meta={<a href={adminHash('articles')}>← All articles</a>} />
        <Trouble error={article.error} />
      </>
    );
  }
  if (article.data === null) {
    return (
      <>
        <Head title="Article" />
        <Card>
          <Loading />
        </Card>
      </>
    );
  }
  return <ArticleEditor key={article.data.updated_at} article={article.data} onChanged={article.reload} />;
}

function formOf(article: Article | null): ArticleForm {
  return {
    title: article?.title ?? '',
    summary: article?.summary ?? '',
    body: article?.body ?? '',
    // No trailing blank: a picture row exists only once its bytes are stored.
    images: [...(article?.images ?? [])],
    sources: [...(article?.sources ?? []), { name: '', url: '' }],
  };
}

function ArticleEditor({ article, onChanged }: { article: Article | null; onChanged?: () => void }) {
  const [form, setForm] = useState<ArticleForm>(() => formOf(article));
  const [problems, setProblems] = useState<Problem[]>([]);
  const [saved, setSaved] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** Blob URLs for files picked in THIS editor, keyed by their stored URL. */
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const createdPreviews = useRef<string[]>([]);
  const intent = useIntentKey();

  // A blob URL pins its file in memory until revoked, and an operator working
  // through a batch of articles would otherwise leak every one of them.
  useEffect(
    () => () => {
      for (const url of createdPreviews.current) URL.revokeObjectURL(url);
    },
    [],
  );

  const create = useAction(
    (body: ArticleBody) => createArticle(body, intent.key),
    (result) => {
      intent.next();
      window.location.hash = adminHash('articles', result.article_id);
    },
  );
  const update = useAction(
    (body: ArticleBody) => (article === null ? Promise.reject(new Error('no article')) : updateArticle(article.article_id, body)),
    () => {
      setSaved(new Date().toISOString());
      onChanged?.();
    },
  );
  const publish = useAction(publishArticle, () => onChanged?.());
  const archive = useAction((articleId: string, reason: string) => archiveArticle(articleId, reason), () => onChanged?.());

  const pending = create.pending || update.pending;
  const set = <K extends keyof ArticleForm>(key: K, value: ArticleForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const setImage = (index: number, patch: Partial<ArticleImageRow>) =>
    set(
      'images',
      form.images.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  const setSource = (index: number, patch: Partial<ArticleSourceRow>) =>
    set(
      'sources',
      form.sources.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );

  /**
   * One picked file: uploaded first, appended second. A row exists only once
   * its bytes are stored, so a saved article can never point at an object that
   * is not there.
   */
  const addPicture = async (file: File) => {
    setUploading(true);
    setUploadError(null);
    try {
      const url = await uploadArticleImage(file);
      const preview = URL.createObjectURL(file);
      createdPreviews.current.push(preview);
      setPreviews((current) => ({ ...current, [url]: preview }));
      setForm((current) => ({ ...current, images: [...current.images, { url, alt: '' }] }));
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : 'The picture could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const submit = () => {
    const result = articleBody(form);
    if (!result.ok) {
      setProblems(result.problems);
      return;
    }
    setProblems([]);
    if (article === null) create.run(result.body);
    else update.run(result.body);
  };

  const bad = (field: string) => (problemFor(problems, field) === undefined ? undefined : { borderColor: 'var(--cr-bad)' });

  return (
    <>
      <Head
        title={article === null ? 'New article' : article.title}
        lede={
          article === null
            ? 'Saved as a draft first — nobody sees it until you publish it.'
            : article.status === 'published'
              ? 'This article is live. Saving changes updates what every user sees immediately.'
              : `This article is ${statusLabel(article.status).toLowerCase()}.`
        }
        meta={<a href={adminHash('articles')}>← All articles</a>}
      />

      {article !== null && (
        <Card title="Status">
          <dl className="cr-kv">
            <dt>Status</dt>
            <dd>
              <Tag tone={statusTone(article.status)}>{statusLabel(article.status)}</Tag>
            </dd>
            <dt>Published</dt>
            <dd>{fmtWhen(article.published_at)}</dd>
            <dt>Created</dt>
            <dd>
              {fmtWhen(article.created_at)} by {show(article.created_by)}
            </dd>
            <dt>Last edited</dt>
            <dd>
              {fmtWhen(article.updated_at)} by {show(article.updated_by)}
            </dd>
          </dl>
          <div className="cr-actions" style={{ marginTop: 14 }}>
            {article.status !== 'published' && (
              <button
                type="button"
                className="cr-btn primary"
                disabled={publish.pending}
                onClick={() => publish.run(article.article_id)}
              >
                {publish.pending ? 'Publishing…' : article.status === 'archived' ? 'Publish again' : 'Publish'}
              </button>
            )}
          </div>
          {article.status !== 'archived' && (
            <ReasonStrip
              verb="Archive"
              tone="danger"
              pending={archive.pending}
              placeholder="Why it should leave the feed"
              onRun={(reason) => archive.run(article.article_id, reason)}
            />
          )}
          {publish.error !== null && <Trouble error={publish.error} />}
          {archive.error !== null && <Trouble error={archive.error} />}
        </Card>
      )}

      <Card title={article === null ? 'Draft' : 'Content'}>
        {saved !== null && (
          <div className="cr-notice ok" role="status">
            <strong>Saved.</strong>
            <span>{fmtWhen(saved)}</span>
          </div>
        )}
        <form
          className="cr-form"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <Field label="Title" hint="up to 200 characters" problem={problemFor(problems, 'title')}>
            <input type="text" maxLength={200} value={form.title} onChange={(event) => set('title', event.target.value)} />
          </Field>
          <Field label="Summary" hint="shown on the card in the feed · up to 500 characters" problem={problemFor(problems, 'summary')}>
            <textarea rows={2} maxLength={500} value={form.summary} onChange={(event) => set('summary', event.target.value)} />
          </Field>
          <Field label="Body" hint="Markdown formatting is supported · up to 20,000 characters" problem={problemFor(problems, 'body')}>
            <textarea
              rows={14}
              maxLength={20_000}
              value={form.body}
              onChange={(event) => set('body', event.target.value)}
              style={{ minHeight: 260 }}
            />
          </Field>

          <div>
            <span className="cr-eyebrow">Pictures</span>
            <p className="cr-fine" style={{ margin: '2px 0 8px' }}>
              Choose a picture from this computer — JPEG, PNG or WebP, under 5 MB, the main one first, up to ten. A
              picture you have just chosen is previewed here; one already saved shows as a link, because the console
              never fetches and displays stored imagery.
            </p>
            <div className="cr-rows">
              {form.images.map((row, index) => (
                <div key={row.url} className="cr-row-line">
                  {previews[row.url] === undefined ? (
                    // A SAVED picture is named, not linked: the bucket is
                    // private, so its object URL answers 403 to a browser —
                    // the app is served a signed GET at read time instead. A
                    // legacy pasted address IS a link, because that one leads
                    // somewhere and seeing where is how an operator decides
                    // what to replace it with.
                    <span className="cr-fine" style={{ flex: 1, overflowWrap: 'anywhere' }}>
                      {isUploadedArticleImage(row.url) ? (
                        `Picture ${String(index + 1)} (saved)`
                      ) : (
                        <>
                          {/* Numbered, because the save refusal names it by
                              position and this row has no input to highlight. */}
                          <strong>Picture {index + 1}</strong> — web address, remove it:{' '}
                          <a href={row.url} target="_blank" rel="noopener noreferrer">
                            {row.url}
                          </a>
                        </>
                      )}
                    </span>
                  ) : (
                    <img
                      src={previews[row.url]}
                      alt=""
                      style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 4 }}
                    />
                  )}
                  <input
                    type="text"
                    placeholder="Description for screen readers"
                    maxLength={300}
                    value={row.alt}
                    style={bad(`images.${String(index)}.alt`)}
                    onChange={(event) => setImage(index, { alt: event.target.value })}
                    aria-label={`Picture ${String(index + 1)} description`}
                  />
                  <button
                    type="button"
                    className="cr-btn ghost"
                    onClick={() =>
                      set(
                        'images',
                        form.images.filter((_, i) => i !== index),
                      )
                    }
                    aria-label={`Remove picture ${String(index + 1)}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              {form.images.some((row) => !isUploadedArticleImage(row.url)) && (
                <p className="cr-fine">
                  A picture above is a web address, from before pictures were uploaded here. It still shows in the
                  app, but the article cannot be saved until it is removed — remove it and add the picture as a
                  file. Adding a new picture does not replace it.
                </p>
              )}
              <div>
                <label className="cr-btn" style={{ display: 'inline-block', cursor: 'pointer' }}>
                  {uploading ? 'Uploading…' : 'Add a picture'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={uploading || form.images.length >= 10}
                    style={{ display: 'none' }}
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      // Cleared, so choosing the same file twice still fires.
                      event.target.value = '';
                      if (file !== undefined) void addPicture(file);
                    }}
                  />
                </label>
                {uploadError !== null && (
                  <span className="cr-fine" style={{ marginLeft: 8 }}>
                    {uploadError}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            <span className="cr-eyebrow">Sources</span>
            <p className="cr-fine" style={{ margin: '2px 0 8px' }}>
              Where the information comes from — a name and a web address (https), up to ten.
            </p>
            <div className="cr-rows">
              {form.sources.map((row, index) => (
                <div key={index} className="cr-row-line">
                  <input
                    type="text"
                    placeholder="Source name"
                    maxLength={120}
                    value={row.name}
                    style={bad(`sources.${String(index)}.name`)}
                    onChange={(event) => setSource(index, { name: event.target.value })}
                    aria-label={`Source ${String(index + 1)} name`}
                  />
                  <input
                    type="url"
                    placeholder="https://…"
                    value={row.url}
                    style={bad(`sources.${String(index)}.url`)}
                    onChange={(event) => setSource(index, { url: event.target.value })}
                    aria-label={`Source ${String(index + 1)} address`}
                  />
                  <button
                    type="button"
                    className="cr-btn ghost"
                    onClick={() =>
                      set(
                        'sources',
                        form.sources.filter((_, i) => i !== index),
                      )
                    }
                    aria-label={`Remove source ${String(index + 1)}`}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <div>
                <button
                  type="button"
                  className="cr-btn"
                  disabled={form.sources.length >= 10}
                  onClick={() => set('sources', [...form.sources, { name: '', url: '' }])}
                >
                  Add a source
                </button>
              </div>
            </div>
          </div>

          <Problems problems={problems} />
          <div className="cr-actions">
            <button type="submit" className="cr-btn primary" disabled={pending}>
              {pending ? 'Saving…' : article === null ? 'Create draft' : 'Save changes'}
            </button>
            {article !== null && article.status === 'published' && (
              <span className="cr-fine">Live — every user sees the saved version straight away.</span>
            )}
          </div>
          {create.error !== null && <Trouble error={create.error} />}
          {update.error !== null && <Trouble error={update.error} />}
        </form>
      </Card>

      {article !== null && (article.images.length > 0 || article.sources.length > 0) && (
        <Card title="Pictures and sources, as saved">
          <p className="cr-card-note">
            Names and links only — the console never displays a picture. An uploaded picture is named rather than
            linked: its bucket is private, and the app is served a short-lived signed address instead.
          </p>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {article.images.map((image, index) => (
              <li key={`image-${String(index)}`}>
                {isUploadedArticleImage(image.url) ? (
                  `Picture ${String(index + 1)} (uploaded)`
                ) : (
                  <a href={image.url} target="_blank" rel="noopener noreferrer">
                    {image.url}
                  </a>
                )}
                {image.alt !== '' && <span className="cr-muted"> — {image.alt}</span>}
              </li>
            ))}
            {article.sources.map((source, index) => (
              <li key={`source-${String(index)}`}>
                {source.name}:{' '}
                <a href={source.url} target="_blank" rel="noopener noreferrer">
                  {source.url}
                </a>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}
